import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { aiAPI } from '../services/api';

const ChatAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Bonjour! Je suis l\'assistant IA d\'EvoCore PACA. Comment puis-je vous aider?',
    },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = { role: 'user', content: input };
    setMessages([...messages, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await aiAPI.chat(input);
      const content = response?.data?.response || "L'assistant IA n'est pas configuré.";
      const assistantMessage = { role: 'assistant', content };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage = { role: 'assistant', content: 'Désolé, une erreur s\'est produite.' };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const sendQuick = async (text) => {
    if (isLoading) return;
    const userMessage = { role: 'user', content: text };
    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);
    try {
      const response = await aiAPI.chat(text);
      const content = response?.data?.response || "L'assistant IA n'est pas configuré.";
      const assistantMessage = { role: 'assistant', content };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage = { role: 'assistant', content: "Désolé, une erreur s'est produite." };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-4 right-4 md:bottom-6 md:right-6 p-3 md:p-4 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-2xl transition-all hover:scale-110 z-50"
          data-testid="open-chat-btn"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed inset-0 m-3 md:m-0 md:inset-auto md:bottom-6 md:right-6 w-full h-[80vh] md:w-96 md:h-[500px] bg-slate-800 rounded-2xl shadow-2xl border border-slate-700 flex flex-col z-50 animate-fadeIn">
          {/* Header */}
          <div className="flex justify-between items-center p-3 md:p-4 border-b border-slate-700">
            <h3 className="text-white font-bold flex items-center gap-2">
              <MessageCircle className="w-4 h-4 md:w-5 md:h-5 text-blue-400" />
              <span className="text-sm md:text-base">Assistant IA</span>
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-white transition-colors"
              data-testid="close-chat-btn"
            >
              <X className="w-4 h-4 md:w-5 md:h-5" />
            </button>
          </div>

          <div className="px-2 md:px-4 pt-2 md:pt-3 flex gap-1 md:gap-2">
            <Button disabled={isLoading} onClick={() => sendQuick('Présentez EvoCore PACA')} className="bg-slate-700 hover:bg-slate-600 text-gray-200 px-2 py-1 text-xs md:text-sm">Présentation</Button>
            <Button disabled={isLoading} onClick={() => sendQuick('Je suis investisseur, comment faire ?')} className="bg-slate-700 hover:bg-slate-600 text-gray-200 px-2 py-1 text-xs md:text-sm">Investisseur</Button>
            <Button disabled={isLoading} onClick={() => sendQuick('Je veux un contact simple')} className="bg-slate-700 hover:bg-slate-600 text-gray-200 px-2 py-1 text-xs md:text-sm">Contact Simple</Button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-2 md:p-4 space-y-2 md:space-y-4">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`chat-message-${index}`}
              >
                <div
                  className={`max-w-[85%] p-2 md:p-3 rounded-lg text-sm md:text-base ${
                    msg.role === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-700 text-gray-200'
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-slate-700 text-gray-200 p-2 md:p-3 rounded-lg">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse" />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-2 md:p-4 border-t border-slate-700">
            <div className="flex gap-1 md:gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Posez votre question..."
                className="flex-1 bg-slate-700 border-slate-600 text-white text-sm md:text-base"
                data-testid="chat-input"
              />
              <Button
                onClick={handleSend}
                disabled={!input.trim() || isLoading}
                className="bg-blue-600 hover:bg-blue-700 px-3 py-2 md:px-4 md:py-2"
                data-testid="chat-send-btn"
              >
                <Send className="w-3 h-3 md:w-4 md:h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatAssistant;
